import {
  departmentInventoryActivityLogsChecked,
  filterLogsByActivityLogs,
} from 'pages/Logs/logUtils';
import {
  selectActivityLogsAllItems,
  selectActivityLogsCashIn,
  selectActivityLogsCashOut,
  selectActivityLogsMergedLogs,
  selectActivityLogsUseTimeRange,
  selectActivityLogsViewAs,
  selectAssociateElevatedActivityLogs,
  selectComplianceActivityStaffId,
  selectIsComplianceActivity,
  selectLogSearch,
  selectLogSearchOptions,
  selectLogSearches,
  selectLogs,
  selectLogsChecked,
  selectScopesChecked,
  selectSingleLogTime,
  setLogsChecked,
  setSingleLogTime,
} from 'pages/Logs/redux';
import { SHOW_ALL_ACTIVITY_LOGS } from 'pages/Logs/activityGroupsModel';
import {
  collectLogSearchQueries,
  filterLogsBySearch,
} from 'pages/Logs/logSearch';
import { selectOrganizationData, selectMetricTime } from 'pages/redux';
import { useHistory, useLocation } from 'react-router-dom';
import { ERP_PATHS } from 'pages/Layout/erpModulePaths';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { selectPermission, selectStaffId, selectUsername } from 'redux/global';
import { selectErpRecords } from 'pages/Records/redux';
import { aggregateLogsToMetricLogs } from './aggregateMetricLogs';
import { buildMetricsActivityLogs } from './buildMetricsActivityLogs';
import { windowLogsToChartLimit } from './metricsChartLimit';
import {
  MetricLog, selectCustomBalanceScopeIds, selectMetricChecked, selectMetricsFlowType
} from './redux';
import { metrics, type MetricsKey } from './metricsKeys';
import React, {
  createContext, useCallback, useContext, useDeferredValue, useMemo,
} from 'react';
import { buildStaffMaps } from 'pages/Logs/logActor';
import { useSettings } from 'pages/Settings/hooks';
import { activityLogsCashFiltersToFlowType } from 'pages/Logs/activityLogsCashFilters';
import { useLogs } from 'pages/Logs/hooks';
import { buildActivityGroupsModel } from 'pages/Logs/activityGroupsModel';
import { resolveMetricsScopeSelection } from './metricsScopeSelection';
import { filterMetricsScopedLogs } from './filterMetricsScopedLogs';
import { metricsChartHasChildScopes } from './metricsScopeCharts';
import { useMetricsActivityLogs } from './useMetricsActivityLogs';
import { buildCustomerMaps } from 'pages/Logs/activityLogsDisplay';

export { metrics, type MetricsKey } from './metricsKeys';

type TimeMetric = {
  total: () => number,
  checked: string[],
  onClick: Function,
  name: MetricsKey,
}

const parseLogTime = (date: string) => {
  const time = Date.parse(date);
  return Number.isNaN(time) ? 0 : time;
};

const MetricsContext = createContext<ReturnType<typeof useComputeMetrics> | null>(null);

const useComputeMetrics = () => {
  const activityLogs = useMetricsActivityLogs();
  const checked = useAppSelector(selectMetricChecked);
  const logsChecked = useAppSelector(selectLogsChecked);
  const deferredChecked = useDeferredValue(checked);
  const chartMetricKey = deferredChecked as MetricsKey;
  const logSearch = useAppSelector(selectLogSearch);
  const logSearchOptions = useAppSelector(selectLogSearchOptions);
  const logSearches = useAppSelector(selectLogSearches);
  const metricTime = useAppSelector(selectMetricTime);
  const storedFlowType = useAppSelector(selectMetricsFlowType);
  const mergedRawLogs = useAppSelector(selectActivityLogsMergedLogs);
  const currentPageLogs = useAppSelector(selectLogs);
  const customBalanceScopeIds = useAppSelector(selectCustomBalanceScopeIds);
  const scopesChecked = useAppSelector(selectScopesChecked);
  const skuChecked = [SHOW_ALL_ACTIVITY_LOGS];
  const permission = useAppSelector(selectPermission);
  const staffId = useAppSelector(selectStaffId);
  const username = useAppSelector(selectUsername);
  const activityLogsViewAs = useAppSelector(selectActivityLogsViewAs);
  const associateElevatedActivityLogs = useAppSelector(selectAssociateElevatedActivityLogs);
  const isComplianceActivity = useAppSelector(selectIsComplianceActivity);
  const complianceActivityStaffId = useAppSelector(selectComplianceActivityStaffId);
  const history = useHistory();
  const dispatch = useAppDispatch();
  const organizationData = useAppSelector(selectOrganizationData);
  const location = useLocation();
  const { users } = useSettings();
  const erpRecords = useAppSelector(selectErpRecords);
  const {
    logGroups,
    activityGroups,
    activityLinks,
    deletedDepartmentNames,
    deletedInventoryItemNames,
    deletedInventoryGroupNames,
  } = useLogs();
  const staffMaps = useMemo(() => buildStaffMaps(users), [users]);
  const customerMaps = useMemo(() => buildCustomerMaps([]), []);
  const activityGroupsModel = useMemo(
    () => buildActivityGroupsModel(activityGroups, {
      links: activityLinks,
      // Scope tree for metrics — inventory + groupTypes only; skip scanning merged logs.
      inventory: organizationData?.inventory,
    }),
    [activityGroups, activityLinks, organizationData?.inventory],
  );
  const metricsScopeSelection = useMemo(
    () => resolveMetricsScopeSelection(
      chartMetricKey,
      activityGroupsModel,
      customBalanceScopeIds,
      { scopesChecked, skuChecked },
    ),
    [chartMetricKey, activityGroupsModel, customBalanceScopeIds, scopesChecked, skuChecked],
  );
  const chartUsesChildScopes = useMemo(
    () => metricsChartHasChildScopes(metricsScopeSelection, activityGroupsModel),
    [metricsScopeSelection, activityGroupsModel],
  );
  const departmentNames = useMemo(
    () => new Set(logGroups.Departments || []),
    [logGroups.Departments],
  );
  const searchArchives = useMemo(() => ({
    deletedTypes: deletedInventoryItemNames,
    deletedGroups: deletedInventoryGroupNames,
    deletedDepartments: deletedDepartmentNames,
  }), [deletedDepartmentNames, deletedInventoryItemNames, deletedInventoryGroupNames]);
  const effectivePermission = activityLogsViewAs?.permission ?? permission;
  const effectiveStaffId = activityLogsViewAs?.staffId ?? staffId;
  const effectiveUsername = activityLogsViewAs?.username ?? username;
  const searchQueries = useMemo(
    () => collectLogSearchQueries(logSearches, logSearch, logSearchOptions),
    [logSearches, logSearch, logSearchOptions],
  );
  const hasActiveSearch = searchQueries.length > 0;

  const isActiveActivityLogsMetric = checked === 'Active audits' || checked === 'Active audits2';

  const permissionFilteredRawLogs = useMemo(() => buildMetricsActivityLogs({
    logs: mergedRawLogs?.length ? mergedRawLogs : (currentPageLogs || []),
    permission: effectivePermission ?? 0,
    staffId: effectiveStaffId,
    username: effectiveUsername,
    users: users || [],
    erpRecords: erpRecords || [],
    logSearches,
    logSearch,
    logSearchOptions,
    staffMaps,
    customerMaps,
    departmentNames,
    searchArchives,
    logGroups,
    activityGroupsModel,
    valueMode: 'value',
    associateElevatedActivityLogs,
    isComplianceActivity,
    complianceActivityStaffId,
    applyActivityLogsSearch: isActiveActivityLogsMetric,
  }), [
    mergedRawLogs,
    currentPageLogs,
    effectivePermission,
    effectiveStaffId,
    effectiveUsername,
    users,
    erpRecords,
    logSearches,
    logSearch,
    logSearchOptions,
    staffMaps,
    customerMaps,
    departmentNames,
    searchArchives,
    logGroups,
    activityGroupsModel,
    associateElevatedActivityLogs,
    isComplianceActivity,
    complianceActivityStaffId,
    isActiveActivityLogsMetric,
  ]);

  const scopedRawLogs = useMemo(() => (
    filterMetricsScopedLogs(
      permissionFilteredRawLogs,
      metricsScopeSelection,
      activityGroupsModel,
    )
  ), [
    permissionFilteredRawLogs,
    metricsScopeSelection,
    activityGroupsModel,
  ]);

  const fullMetricLogs = useMemo(
    () => aggregateLogsToMetricLogs(permissionFilteredRawLogs),
    [permissionFilteredRawLogs],
  );

  const metricLogs = useMemo(
    () => aggregateLogsToMetricLogs(scopedRawLogs),
    [scopedRawLogs],
  );

  const isActiveActivityLogsCount = checked === 'Active audits2';
  // Only Active logs share /logs time; every other metric uses Metrics time only.
  const useActivityLogsTime = isActiveActivityLogsMetric;
  const singleLogTime = useAppSelector(selectSingleLogTime);
  const activityLogsUseTimeRange = useAppSelector(selectActivityLogsUseTimeRange);
  const activityLogsAllItems = useAppSelector(selectActivityLogsAllItems);
  const activityLogsCashIn = useAppSelector(selectActivityLogsCashIn);
  const activityLogsCashOut = useAppSelector(selectActivityLogsCashOut);
  const activityLogsFlowType = activityLogsCashFiltersToFlowType({
    allItems: activityLogsAllItems,
    cashIn: activityLogsCashIn,
    cashOut: activityLogsCashOut,
  });
  const activityLogsTimeRangeOn = Boolean(
    activityLogsUseTimeRange
    && singleLogTime?.startDate
    && singleLogTime?.endDate,
  );
  const { startDate: metricStart, endDate: metricEnd } = metricTime || {};
  // Active logs: /logs range when Time is on. All other metrics: Metrics time only.
  const rangeStart = useActivityLogsTime
    ? (activityLogsTimeRangeOn ? singleLogTime!.startDate : undefined)
    : metricStart;
  const rangeEnd = useActivityLogsTime
    ? (activityLogsTimeRangeOn ? singleLogTime!.endDate : undefined)
    : metricEnd;

  const rangeStartMs = rangeStart ? +new Date(rangeStart) : undefined;
  const rangeEndMs = rangeEnd ? +new Date(rangeEnd) : undefined;
  const inMetricTimeRange = useCallback((date: string) => (
    (rangeStartMs == null || rangeEndMs == null)
    || (parseLogTime(date) >= rangeStartMs && parseLogTime(date) <= rangeEndMs)
  ), [rangeStartMs, rangeEndMs]);

  // Charts / date pickers: Metrics time, except Active logs which follow /logs when Time is on.
  const chartTime = useMemo(() => {
    if (useActivityLogsTime) {
      if (activityLogsTimeRangeOn) {
        return {
          startDate: singleLogTime!.startDate,
          endDate: singleLogTime!.endDate,
        };
      }
      if (fullMetricLogs.length) {
        const times = fullMetricLogs.map((log) => +new Date(log.date));
        return {
          startDate: new Date(Math.min(...times)),
          endDate: new Date(Math.max(...times)),
        };
      }
      return metricTime;
    }
    return metricTime;
  }, [
    useActivityLogsTime,
    activityLogsTimeRangeOn,
    singleLogTime,
    fullMetricLogs,
    metricTime,
  ]);

  const flowType = isActiveActivityLogsMetric && !isActiveActivityLogsCount
    ? activityLogsFlowType
    : storedFlowType;

  const allActivityLogs: string[] = useMemo(() => {
    const names: string[] = [...Object.entries(logGroups).map(([k]) => k), 'Show all'];
    Object.entries(logGroups).forEach(([, v]) => names.push(...v));
    return names;
  }, [logGroups]);
  const timeDateFilteredLogs = useMemo(
    () => fullMetricLogs.filter((log) => inMetricTimeRange(log.date)),
    [fullMetricLogs, inMetricTimeRange],
  );

  const searchFilteredDateLogs = useMemo(() => {
    if (!hasActiveSearch) return timeDateFilteredLogs;
    const rawInRange = permissionFilteredRawLogs.filter((log) => inMetricTimeRange(log.date));
    const searched = filterLogsBySearch(
      rawInRange,
      searchQueries,
      staffMaps,
      searchArchives,
      departmentNames,
    );
    return aggregateLogsToMetricLogs(searched);
  }, [
    hasActiveSearch,
    timeDateFilteredLogs,
    permissionFilteredRawLogs,
    inMetricTimeRange,
    searchQueries,
    staffMaps,
    searchArchives,
    departmentNames,
  ]);

  const dateFilteredLogs = useMemo(
    () => metricLogs.filter((log) => inMetricTimeRange(log.date)),
    [metricLogs, inMetricTimeRange],
  );

  // Active logs totals follow the Groups + SKU scoped pool (same pick as /logs).
  const sumScopedMetricLogs = (rows: MetricLog[]) => rows.reduce((a, b) => {
    const n = Number(b.value) || 0;
    const t = flowType === '-' && n < 0 ? n
      : flowType === '+' && n > 0 ? n
        : flowType === 'both' ? n
          : (b.count || 0);
    return a + t;
  }, 0);

  const getTotal = (logsChecked1: string[], useSearch = false) => {
    const pool = useSearch ? searchFilteredDateLogs : timeDateFilteredLogs;
    const matched = filterLogsByActivityLogs(pool, logsChecked1);
    return matched.reduce((a, b) => {
      const t = flowType === '-' && Number(b.value) < 0 ? Number(b.value) :
        flowType === '+' && Number(b.value) > 0 ? Number(b.value) :
          flowType === 'both' ? Number(b.value) || 0 : 0;
      return a + t;
    }, 0);
  };

  const getCount = (logsChecked1: string[], useSearch = false) => {
    const pool = useSearch ? searchFilteredDateLogs : timeDateFilteredLogs;
    const matched = filterLogsByActivityLogs(pool, logsChecked1);
    return matched.reduce((a, b) => a + (b.count || 0), 0);
  };

  const onClickViewMetricActivityLogs = () => {
    // Copy Metrics time onto /logs only when jumping from a non–Active-logs metric.
    if (!useActivityLogsTime && rangeStart && rangeEnd) {
      dispatch(setSingleLogTime({
        startDate: new Date(rangeStart),
        endDate: new Date(rangeEnd),
      }));
    }
    const jumpFlow = isActiveActivityLogsMetric ? activityLogsFlowType : storedFlowType;
    jumpFlow === '-' ? history.push(ERP_PATHS.activityLogsAction('svg1ere2xxyv')) :
      jumpFlow === '+' ? history.push(ERP_PATHS.activityLogsAction('svg1ere1xxyv')) :
        jumpFlow === 'both' ? history.push(ERP_PATHS.activityLogsAction('svg1ere3xxyv')) :
          history.push(ERP_PATHS.activityLogsAction('svg1ere4xxyv'));
  };

  const allBalances = useMemo(() => [
    ...logGroups.Departments,
    ...logGroups.Inventory,
    ...logGroups['Inventory Group'],
    'Departments',
    'Department entries',
    'Direct',
    'Expense',
    'Document',
    'Inventory',
    'Inventory Group',
  ], [logGroups.Departments, logGroups.Inventory, logGroups['Inventory Group']]);
  const departmentsChecked = useMemo(
    () => [...logGroups.Departments, ...logGroups['Department entries']],
    [logGroups.Departments, logGroups['Department entries']],
  );

  const timeMetrics: Record<MetricsKey, TimeMetric> = {
    'Active audits': {
      name: 'Active audits',
      total: () => sumScopedMetricLogs(dateFilteredLogs),
      checked: logsChecked,
      onClick: () => {
        onClickViewMetricActivityLogs();
      }
    },
    Departments: {
      name: 'Departments',
      total: () => getTotal(departmentsChecked),
      checked: departmentsChecked,
      onClick: () => {
        dispatch(setLogsChecked(departmentInventoryActivityLogsChecked(
          logGroups.Departments,
          logGroups,
        )));
        if (rangeStart && rangeEnd) {
          dispatch(setSingleLogTime({
            startDate: new Date(rangeStart),
            endDate: new Date(rangeEnd),
          }));
        }
        history.push(ERP_PATHS.activityLogsAction('svg1ere1xxyv'));
      }
    },
    'All balances': {
      name: 'All balances',
      total: () => getTotal(allBalances),
      checked: allBalances,
      onClick: () => {
        dispatch(setLogsChecked(allBalances));
        onClickViewMetricActivityLogs();
      }
    },
    'Active audits2': {
      name: 'Active audits2',
      total: () => dateFilteredLogs.reduce((a, b) => a + (b.count || 0), 0),
      checked: logsChecked,
      onClick: () => {
        onClickViewMetricActivityLogs();
      }
    },
    Employees: {
      name: 'Employees',
      total: () => getCount(allActivityLogs),
      checked: allActivityLogs,
      onClick: () => {
        dispatch(setLogsChecked(allActivityLogs));
        onClickViewMetricActivityLogs();
      }
    },
    'All audits': {
      name: 'All audits',
      total: () => getCount(allActivityLogs),
      checked: allActivityLogs,
      onClick: () => {
        dispatch(setLogsChecked(allActivityLogs));
        onClickViewMetricActivityLogs();
      }
    },
  };

  const checkedMetric = timeMetrics[checked as MetricsKey] || timeMetrics['All audits'];
  const isEmployeesMetric = checked === 'Employees';
  const isChartEmployeesMetric = chartMetricKey === 'Employees';

  const isDashBoard = location.pathname.includes('dashboard');

  // MetricsTime range bar — full permission pool + activity-log checked tokens.
  const timeUserMetricLogs = useMemo(() => {
    if (!isEmployeesMetric) return fullMetricLogs;
    return aggregateLogsToMetricLogs(
      permissionFilteredRawLogs.filter((log) => inMetricTimeRange(log.date)),
      { byActor: true },
    );
  }, [isEmployeesMetric, fullMetricLogs, permissionFilteredRawLogs, inMetricTimeRange]);

  const timeMetricLogsForCharts = isEmployeesMetric ? timeUserMetricLogs : fullMetricLogs;

  // Active logs: all scoped rows (not the current date window). The time
  // arrows need the full span, same as other metrics, or the range shrinks.
  const checkedMetricLogsAll = useMemo(() => (
    isActiveActivityLogsMetric
      ? metricLogs
      : filterLogsByActivityLogs(timeMetricLogsForCharts, checkedMetric.checked)
  ), [
    isActiveActivityLogsMetric,
    metricLogs,
    timeMetricLogsForCharts,
    checkedMetric.checked,
  ]);

  // Employees charts need created_by kept; default metric buckets merge staff together.
  const userMetricLogs = useMemo(() => {
    if (!isChartEmployeesMetric) return metricLogs;
    const inRange = scopedRawLogs.filter((log) => inMetricTimeRange(log.date));
    return aggregateLogsToMetricLogs(inRange, { byActor: true });
  }, [isChartEmployeesMetric, metricLogs, scopedRawLogs, inMetricTimeRange]);

  const userDateFilteredLogs = useMemo(() => {
    if (!isChartEmployeesMetric) return dateFilteredLogs;
    return userMetricLogs.filter((log) => inMetricTimeRange(log.date));
  }, [isChartEmployeesMetric, dateFilteredLogs, userMetricLogs, inMetricTimeRange]);

  const metricLogsForCharts = isChartEmployeesMetric ? userMetricLogs : metricLogs;
  const dateLogsForCharts = isChartEmployeesMetric ? userDateFilteredLogs : dateFilteredLogs;

  const sortedLogs = useMemo(() => {
    const clipped = checkedMetricLogsAll.filter((log) => {
      const t = parseLogTime(log.date);
      if (rangeStartMs != null && t < rangeStartMs) return false;
      if (rangeEndMs != null && t > rangeEndMs) return false;
      return true;
    });
    return [...clipped].sort((a, b) => parseLogTime(b.date) - parseLogTime(a.date));
  }, [checkedMetricLogsAll, rangeStartMs, rangeEndMs]);
  const maxLog = sortedLogs[sortedLogs.length - 1];
  const maxLogTime = maxLog ? parseLogTime(maxLog.date) : 0;

  const checkFilteredLogs = useMemo(() => {
    const limited = windowLogsToChartLimit(dateLogsForCharts);
    const windowed = limited.filter((l) => (
      isDashBoard || parseLogTime(l.date) >= maxLogTime
    ));
    return [...windowed].sort((a, b) => parseLogTime(a.date) - parseLogTime(b.date));
  }, [
    dateLogsForCharts,
    isDashBoard,
    maxLogTime,
  ]);

  // Not use in dashboard(below)
  const metricTimeLogs = useMemo(() => (
    sortedLogs
      .filter((log) => !maxLogTime || parseLogTime(log.date) >= maxLogTime)
      .sort((a, b) => parseLogTime(a.date) - parseLogTime(b.date))
  ), [sortedLogs, maxLogTime]);

  const getFlowTypeValue = useCallback((log: MetricLog) => {
    const { value, count } = log;
    return flowType === '+' ? (Number(value) || 0) > 0 ? Number(value) : 0 :
      flowType === '-' ? (Number(value) || 0) < 0 ? Number(value) : 0 :
        flowType === 'both' ? (Number(value) || 0) : count;
  }, [flowType]);

  const checkedMetricTotal = useMemo(
    () => checkedMetric.total(),
    [
      checked,
      flowType,
      timeDateFilteredLogs,
      searchFilteredDateLogs,
      dateFilteredLogs,
      checkedMetric.checked,
      logsChecked,
    ],
  );
  const checkedMetricWithCachedTotal = useMemo(() => ({
    ...checkedMetric,
    total: () => checkedMetricTotal,
  }), [checkedMetric, checkedMetricTotal]);

  // eslint-disable-next-line no-unused-vars
  const getMetricValue = useCallback((criterea: (log: MetricLog) => boolean, logToUse?: string) => {
    const l = logToUse === 'logs' ? metricLogs : checkFilteredLogs;
    return (l || []).reduce((a, b) => {
      return a + (criterea(b) ? getFlowTypeValue(b) : 0);
    }, 0);
  }, [checkFilteredLogs, metricLogs, getFlowTypeValue]);

  return {
    logGroups,
    timeMetrics,
    checked,
    checkedMetric: checkedMetricWithCachedTotal,
    dateFilteredLogs,
    checkFilteredLogs,
    checkedMetricLogsAll,
    metricTimeLogs,
    metricTime,
    getMetricValue,
    getFlowTypeValue,
    getCount,
    flowType,
    storedFlowType,
    activityLogsFlowType,
    isActiveActivityLogsMetric,
    useActivityLogsTime,
    activityLogsUseTimeRange,
    activityLogsTimeRangeOn,
    chartTime,
    allActivityLogs,
    metricChecked: checked,
    metricLogs,
    staffMaps,
    activityGroupsModel,
    metricsScopeSelection,
    chartUsesChildScopes,
    activityLogs,
  };
};

export const MetricsProvider = ({ children }: { children: React.ReactNode }) => {
  const value = useComputeMetrics();
  return React.createElement(MetricsContext.Provider, { value }, children);
};

export const useMetrics = () => {
  const shared = useContext(MetricsContext);
  if (!shared) {
    throw new Error('useMetrics must be used within MetricsProvider');
  }
  return shared;
};

export const useMetricLogs = () => {
  return {};
};
